import { Component, HostListener, Input } from "@angular/core";
import { Router } from "@angular/router";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { AgreementEligibilityCode } from "@edp-enums/agreement-eligibility.enum";
import { SCREEN_SIZES } from "@edp-enums/screen-sizes.enum";
import { PaymentAgreement, PaymentAgreementError, SelectedComponentEnum } from "@edp-models/edp-payment-agreement.model";
import { AgreementContactResponse, AgreementEligibilityError } from "@edp-models/edp-payment.model";
import { EdpUser } from "@edp-models/user.model";
import { EdpPaymentAgreementService } from "@edp-services/edp-payment-agreement.service";
import { EdpSteperService } from "@edp-services/edp-steper.service";
import { NifService } from "@edp-services/nif.service";
import { PaymentService } from "@edp-services/payment.service";
import { getUser } from "@edp-store/selectors/user.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { Store, select } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { AnimationOptions } from "ngx-lottie";
import { Subject, interval } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "waiting-verification",
    templateUrl: "./waiting-verification.component.html",
    styleUrls: ["./waiting-verification.component.scss"],
})
export class WaitingVerificationComponent {
    @Input() hasRedirect: boolean;
    @Input() onCloseRedirectUrl: string;

    public mobileSize: boolean = window.innerWidth <= SCREEN_SIZES.MD;
    private destroy$: Subject<void> = new Subject<void>();
    private timerInterval$: any;
    public nif: string;
    public accountId: string;
    public isPage: boolean;
    public contact: AgreementContactResponse;
    public user: EdpUser;
    public fireRedirect: boolean = true;

    public CHECK_ANALISYS_INTERVAL = 30000;
    public REDIRECT_AFTER_INTERVAL = 120000;

    public SHOW_TOAST_AFTER_INTERVAL = 90000;

    public options: AnimationOptions = {
        path: "/assets/images/waiting-verification.json",
        loop: true,
    };

    constructor(
        private paymentService: PaymentService,
        private nifService: NifService,
        private edpPaymentAgreementService: EdpPaymentAgreementService,
        private router: Router,
        private edpSteperService: EdpSteperService,
        private toasterService: EdpToasterService,
        private readonly translateService: TranslateService,
        private store: Store<IAppState>
    ) {
        this.nif = this.nifService.getNifValue().value;
        this.accountId = this.edpPaymentAgreementService.getAccount();
        this.isPage = this.edpPaymentAgreementService.getBottomBarValue();
        this.contact = this.edpPaymentAgreementService.getContactResponse();

        this.store.pipe(select(getUser)).subscribe((user: EdpUser) => {
            if (!!user) {
                this.user = user;
            }
        });
    }

    ngOnInit() {
        this.statusAnalisysInterval();

        if (!!this.contact?.contact?.email) {
            setTimeout(() => {
                if (this.fireRedirect) {
                    this.showToast();
                }
            }, this.SHOW_TOAST_AFTER_INTERVAL);
        }

        this.edpPaymentAgreementService.killWaitingVerificationTrigger$.subscribe(() => {
            this.fireRedirect = false;
            this.destroyRedirectProcess();
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public callAgreementCheckout() {
        const args = {
            nif: this.nif,
            account: this.accountId,
        };
        this.paymentService.agreementCheckout(args).subscribe(
            (response: any) => {
                const res = response[0] as PaymentAgreement;
                switch (true) {
                    case res.code === AgreementEligibilityCode.STATUS_APROVED:
                        this.edpPaymentAgreementService.setPaymentAgreementTerm(res);
                        this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.PAYMENT_AGREEMENT_TERM);
                        this.fireRedirect = false;
                        this.destroyRedirectProcess();
                        break;
                    case !res.status && res.error === PaymentAgreementError.EXPIRATE_PROCESS:
                        this.edpSteperService.setActiveStep(3);
                        this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.AGREEMENT_EXPIRED);
                        this.fireRedirect = false;
                        this.destroyRedirectProcess();
                        break;
                    case !res.status && res.error === PaymentAgreementError.ERROR_PROCESS:
                        this.edpPaymentAgreementService.errorProccessSetStatus(true);
                        this.fireRedirect = false;
                        this.destroyRedirectProcess();
                        break;
                    case !res.status &&
                        (res.data.error === AgreementEligibilityError.E001 ||
                            res.data.error === AgreementEligibilityError.E002 ||
                            res.data.error === AgreementEligibilityError.E003 ||
                            res.data.error === AgreementEligibilityError.E006 ||
                            res.data.error === AgreementEligibilityError.E007 ||
                            res.data.error === AgreementEligibilityError.E008 ||
                            res.data.error === AgreementEligibilityError.E009 ||
                            res.data.error === AgreementEligibilityError.E010 ||
                            res.data.error === AgreementEligibilityError.E011):
                        this.destroyRedirectProcess();
                        this.fireRedirect = false;
                        this.edpPaymentAgreementService.setIneligibleClient(true);
                        this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.NO_AGREEMENT);
                        break;
                    case !res.status &&
                        (res.data.error === AgreementEligibilityError.E004 || res.data.error === AgreementEligibilityError.E005):
                        this.destroyRedirectProcess();
                        this.fireRedirect = false;
                        this.edpPaymentAgreementService.setIneligibleClient(false);
                        this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.NO_AGREEMENT);
                        break;
                    default:
                        break;
                }
            },
            error => {
                this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.GENERIC_ERROR);
                console.error(error);
            }
        );
    }

    public destroyRedirectProcess() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public redirect() {
        if (!this.user) {
            this.router.navigate(["/area-aberta/acordo-pagamento/delay"]);
        } else {
            this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.ELIGIBILITY_DELAY);
        }
    }

    @HostListener("window:resize", ["$event"])
    public onResize(event: Event) {
        this.updateMobileSize();
    }

    public updateMobileSize() {
        this.mobileSize = window.innerWidth <= SCREEN_SIZES.MD;
    }

    public close() {
        const path = `/${this.onCloseRedirectUrl}`;
        this.router.navigate([path]);

        if (!this.edpPaymentAgreementService.errorProccessGetToShow() && this.isPage) {
            this.validateToastType();
        }
    }

    public showToast() {
        const label = this.translateService.instant("pt.edp.edponline.waiting.verification.toast.label", {
            EMAIL: this.contact?.contact?.email,
        });
        this.toasterService
            .create(label, EdpToastType.info, {
                functionCloseButton: () => this.goToEligibilityDelay(),
                showLeftIcon: false,
                showCloseModalButton: true,
                textCloseModalButton: this.translateService.instant("pt.edp.edponline.waiting.verification.toast.redirect.label"),
                timeout: 3000,
            })
            .show();
    }

    private statusAnalisysInterval() {
        this.callAgreementCheckout();

        this.timerInterval$ = interval(this.CHECK_ANALISYS_INTERVAL)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.callAgreementCheckout();
            });

        setTimeout(() => {
            if (this.fireRedirect) {
                this.timerInterval$.unsubscribe();
                this.validateAssociatedEmail();
                return;
            }
        }, this.REDIRECT_AFTER_INTERVAL);
    }

    private validateAssociatedEmail() {
        if (!!this.contact && !this.contact?.contact?.email) {
            this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.NO_ASSOCIATED_EMAIL);
        } else {
            this.redirect();
        }
    }

    private goToEligibilityDelay() {
        this.destroy$.next();
        this.destroy$.complete();
        this.edpPaymentAgreementService.setSelectedComponent(SelectedComponentEnum.ELIGIBILITY_DELAY);
    }

    private validateToastType() {
        this.edpPaymentAgreementService.errorProccessSetToShow(true);
        if (this.edpPaymentAgreementService.errorProccessGetStatus()) {
            this.showToastError();
        } else {
            this.showToastSucess();
        }
    }

    private showToastSucess() {
        const label = this.translateService.instant("pt.edp.edponline.waiting.verification.toast.sucess.label");
        this.toast(label, EdpToastType.success);
    }

    private showToastError() {
        const label = this.translateService.instant("pt.edp.edponline.waiting.verification.toast.error.label");
        this.toast(label, EdpToastType.error);
    }

    private toast(label: string, type) {
        this.toasterService
            .create(label, type, {
                showLeftIcon: true,
                timeout: 3000,
            })
            .show();
    }
}
